import { Card } from "@pericles/framework-frontend";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  acknowledgeLongwallsJarvisIncident,
  approveLongwallsJarvisApproval,
  executeLongwallsJarvisOvernight,
  fetchLongwallsJarvisAdapterHealth,
  fetchLongwallsJarvisApprovals,
  fetchLongwallsJarvisBridgeOverview,
  fetchLongwallsJarvisIncidents,
  fetchLongwallsJarvisLearning,
  fetchLongwallsJarvisOperations,
  fetchLongwallsJarvisPrograms,
  fetchLongwallsJarvisReleaseState,
  fetchLongwallsJarvisWeeklyBoard,
  rejectLongwallsJarvisApproval,
  replayLongwallsJarvisOperation,
  resolveLongwallsJarvisIncident,
  type LongwallsJarvisAdapterRecord,
  type LongwallsJarvisApprovalRecord,
  type LongwallsJarvisBridgeOverview,
  type LongwallsJarvisIncidentRecord,
  type LongwallsJarvisOperationRecord,
  type LongwallsJarvisProgramRecord,
  type LongwallsJarvisReleaseState,
} from "../api/jarvisControlBridgeClient";
import {
  bootstrapLongwallsPilotChain,
  fetchLongwallsJarvisDefaultUsagePack,
  fetchLongwallsPilotChain,
  fetchLongwallsPilotGate,
  fetchLongwallsPilotPack,
  finalizeLongwallsPilotChain,
  runLongwallsPilotV1,
  submitLongwallsPilotFeedback,
  type LongwallsJarvisDefaultUsagePack,
  type LongwallsEnterprisePilotGate,
  type LongwallsEnterprisePilotPack,
  type LongwallsPilotChainRecord,
} from "../api/longwallsEnterpriseClient";

import { Button } from "./ui";

function statusLabel(status: LongwallsJarvisBridgeOverview["connection"]["status"]) {
  if (status === "connected") return "Connected";
  if (status === "degraded") return "Degraded";
  return "Offline";
}

function pilotPackStatusTone(status: LongwallsEnterprisePilotPack["summary"]["status"]) {
  return status === "pilot_ready" ? "connected" : "degraded";
}

function pilotPackStatusLabel(status: LongwallsEnterprisePilotPack["summary"]["status"]) {
  return status === "pilot_ready" ? "Connected" : "Degraded";
}

function buildPilotPackFallbackHeadline(
  status: LongwallsEnterprisePilotPack["summary"]["status"],
  projectId: string,
) {
  if (status === "pilot_ready") {
    return `The live pilot gate for ${projectId} is green while the packaged pilot dossier refreshes.`;
  }
  return "Pilot pack generation is pending or degraded.";
}

function brickSelectionTone(selection: "required" | "recommended" | "optional" | "rejected") {
  if (selection === "required") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (selection === "recommended") return "border-blue-200 bg-blue-50 text-blue-700";
  if (selection === "optional") return "border-slate-200 bg-slate-50 text-slate-700";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function toDisplayList(value: string): string[] {
  return value
    .split(/\r?\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

type PilotChainMutationOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  optimisticState?: LongwallsPilotChainRecord["state"];
  optimisticNextAction?: string;
};

type BridgeSnapshot = {
  overview: LongwallsJarvisBridgeOverview;
  approvals: LongwallsJarvisApprovalRecord[];
  programs: LongwallsJarvisProgramRecord[];
  incidents: LongwallsJarvisIncidentRecord[];
  operations: LongwallsJarvisOperationRecord[];
  adapters: LongwallsJarvisAdapterRecord[];
  releaseState: LongwallsJarvisReleaseState | null;
  defaultUsage: LongwallsJarvisDefaultUsagePack | null;
  learningSummary: string[];
  weeklyBoardLaneCount: number | null;
};

function fallbackBridgeSnapshot(overview: LongwallsJarvisBridgeOverview): BridgeSnapshot {
  return {
    overview,
    approvals: [],
    programs: [],
    incidents: [],
    operations: [],
    adapters: overview.adapters,
    releaseState: null,
    defaultUsage: null,
    learningSummary: [],
    weeklyBoardLaneCount: null,
  };
}

function buildUnavailableOverview(message: string): LongwallsJarvisBridgeOverview {
  return {
    generatedAt: new Date().toISOString(),
    connection: {
      status: "offline",
      summary: message,
      apiBaseUrl: "",
      uiBaseUrl: null,
      internalKeyConfigured: false,
      reachableSlices: [],
      failedSlices: [{ id: "overview", reason: message }],
    },
    readiness: {
      overallScore: null,
      releaseDecision: null,
      truthStatement: null,
      nextLeverage: null,
      greenGates: 0,
      watchGates: 0,
      riskGates: 0,
    },
    founder: {
      pendingApprovals: 0,
      openIncidents: 0,
      leverageScore: null,
      criticalSignals: [],
      overnightHeadline: null,
    },
    tactical: {
      programsTracked: 0,
      totalOpenTasks: 0,
      blockedTasks: 0,
      founderPendingTasks: 0,
      recentOperations: 0,
      defaultLane: null,
    },
    operator: {
      totalAdapters: 0,
      healthyAdapters: 0,
      degradedAdapters: 0,
      activeIncidents: 0,
      authProvider: null,
      storeBackend: null,
    },
    security: {
      breakGlassActive: false,
      globalWritesDisabled: false,
      dangerousWritesDisabled: false,
      cloudflareWriteScope: null,
      vercelWriteScope: null,
      credentialStatus: [],
    },
    links: {
      uiBaseUrl: null,
      founder: null,
      tactical: null,
      operator: null,
      strategy: null,
    },
    adapters: [],
    approvals: [],
    incidents: [],
  };
}

function buildLoadingOverview(): LongwallsJarvisBridgeOverview {
  return {
    generatedAt: new Date().toISOString(),
    connection: {
      status: "degraded",
      summary: "Loading Jarvis bridge...",
      apiBaseUrl: "",
      uiBaseUrl: null,
      internalKeyConfigured: false,
      reachableSlices: [],
      failedSlices: [],
    },
    readiness: {
      overallScore: null,
      releaseDecision: null,
      truthStatement: "Jarvis bridge is still loading.",
      nextLeverage: null,
      greenGates: 0,
      watchGates: 0,
      riskGates: 0,
    },
    founder: {
      pendingApprovals: 0,
      openIncidents: 0,
      leverageScore: null,
      criticalSignals: [],
      overnightHeadline: null,
    },
    tactical: {
      programsTracked: 0,
      totalOpenTasks: 0,
      blockedTasks: 0,
      founderPendingTasks: 0,
      recentOperations: 0,
      defaultLane: null,
    },
    operator: {
      totalAdapters: 0,
      healthyAdapters: 0,
      degradedAdapters: 0,
      activeIncidents: 0,
      authProvider: null,
      storeBackend: null,
    },
    security: {
      breakGlassActive: false,
      globalWritesDisabled: false,
      dangerousWritesDisabled: false,
      cloudflareWriteScope: null,
      vercelWriteScope: null,
      credentialStatus: [],
    },
    links: {
      uiBaseUrl: null,
      founder: null,
      tactical: null,
      operator: null,
      strategy: null,
    },
    adapters: [],
    approvals: [],
    incidents: [],
  };
}

function buildPilotPackArtifactUrls(projectId: string) {
  const base = `/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-pack/artifacts`;
  return {
    pilotPackJson: `${base}/pilot_pack.json`,
    pilotPackMarkdown: `${base}/pilot_pack.md`,
    roleGuide: `${base}/pilot_role_guide.md`,
    supportRunbook: `${base}/pilot_support_runbook.md`,
    deploymentKit: `${base}/pilot_deployment_kit.md`,
  };
}

function buildPilotChainStorageKey(projectId: string) {
  return `longwalls.jarvis.pilotChain.${projectId}`;
}

function readStoredPilotChain(projectId: string): LongwallsPilotChainRecord | null {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(buildPilotChainStorageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isPilotChainRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistPilotChain(projectId: string, pilotChain: LongwallsPilotChainRecord | null) {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  try {
    const storageKey = buildPilotChainStorageKey(projectId);
    if (!pilotChain) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }
    window.sessionStorage.setItem(storageKey, JSON.stringify(pilotChain));
  } catch {
    // Ignore storage failures; the backend remains the source of truth.
  }
}

function isPilotChainRecord(value: unknown): value is LongwallsPilotChainRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { projectId?: unknown; state?: unknown; nextAction?: unknown };
  return (
    typeof candidate.projectId === "string" &&
    typeof candidate.state === "string" &&
    typeof candidate.nextAction === "string"
  );
}

const PILOT_CHAIN_STAGE_ORDER: LongwallsPilotChainRecord["state"][] = [
  "not_started",
  "bootstrapped",
  "v1_running",
  "v1_ready",
  "feedback_received",
  "finalizing",
  "finalized",
];

function hasReachedPilotChainStage(
  state: LongwallsPilotChainRecord["state"],
  targets: LongwallsPilotChainRecord["state"][],
): boolean {
  if (state === "blocked") return targets.includes("blocked");
  if (targets.includes(state)) return true;

  const stateRank = PILOT_CHAIN_STAGE_ORDER.indexOf(state);
  if (stateRank < 0) return false;

  return targets.some((target) => {
    if (target === "blocked") return false;
    const targetRank = PILOT_CHAIN_STAGE_ORDER.indexOf(target);
    return targetRank >= 0 && stateRank >= targetRank;
  });
}

export function JarvisLandingScreen(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useMemo(() => new URLSearchParams(location.search).get("projectId")?.trim() ?? "", [location.search]);
  const refreshRequestRef = useRef(0);
  const lastProjectIdRef = useRef<string | null>(null);

  const [snapshot, setSnapshot] = useState<BridgeSnapshot | null>(null);
  const [pilotGate, setPilotGate] = useState<LongwallsEnterprisePilotGate | null>(null);
  const [pilotChain, setPilotChain] = useState<LongwallsPilotChainRecord | null>(() =>
    projectId ? readStoredPilotChain(projectId) : null,
  );
  const [pilotPack, setPilotPack] = useState<LongwallsEnterprisePilotPack | null>(null);
  const [isPilotGateLoading, setIsPilotGateLoading] = useState(false);
  const [isPilotChainLoading, setIsPilotChainLoading] = useState(false);
  const [isPilotPackLoading, setIsPilotPackLoading] = useState(false);
  const [feedbackSummary, setFeedbackSummary] = useState("");
  const [feedbackRequestedChanges, setFeedbackRequestedChanges] = useState("");
  const [feedbackAcceptanceNotes, setFeedbackAcceptanceNotes] = useState("");
  const [feedbackRisks, setFeedbackRisks] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const refresh = async () => {
    const requestId = ++refreshRequestRef.current;
    const normalizedProjectId = projectId || null;
    const projectChanged = lastProjectIdRef.current !== normalizedProjectId;
    lastProjectIdRef.current = normalizedProjectId;
    setIsLoading(true);
    setError(null);
    setActionMessage(null);

    if (projectId) {
      if (projectChanged) {
        setPilotGate(null);
        setPilotChain(readStoredPilotChain(projectId));
        setPilotPack(null);
      }
      setIsPilotGateLoading(true);
      setIsPilotChainLoading(true);
      setIsPilotPackLoading(true);
      setSnapshot((current) => current ?? fallbackBridgeSnapshot(buildLoadingOverview()));

      void fetchLongwallsPilotGate(projectId)
        .then((payload) => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotGate(payload);
        })
        .catch(() => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotGate(null);
        })
        .finally(() => {
          if (refreshRequestRef.current !== requestId) return;
          setIsPilotGateLoading(false);
        });

      void fetchLongwallsPilotChain(projectId)
        .then((payload) => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotChain(payload);
        })
        .catch(() => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotChain((current) => current);
        })
        .finally(() => {
          if (refreshRequestRef.current !== requestId) return;
          setIsPilotChainLoading(false);
        });

      void fetchLongwallsPilotPack(projectId)
        .then((pack) => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotPack(pack);
        })
        .catch(() => {
          if (refreshRequestRef.current !== requestId) return;
          setPilotPack(null);
        })
        .finally(() => {
          if (refreshRequestRef.current !== requestId) return;
          setIsPilotPackLoading(false);
        });
    } else {
      lastProjectIdRef.current = null;
      setPilotGate(null);
      setPilotChain(null);
      setPilotPack(null);
      setIsPilotGateLoading(false);
      setIsPilotChainLoading(false);
      setIsPilotPackLoading(false);
    }

    try {
      const overview = await fetchLongwallsJarvisBridgeOverview();
      if (refreshRequestRef.current !== requestId) return;
      setSnapshot(fallbackBridgeSnapshot(overview));
      setIsLoading(false);

      const [
        approvalsResult,
        programsResult,
        incidentsResult,
        operationsResult,
        adaptersResult,
        releaseStateResult,
        defaultUsageResult,
        learningResult,
        weeklyBoardResult,
      ] = await Promise.allSettled([
        fetchLongwallsJarvisApprovals(),
        fetchLongwallsJarvisPrograms(),
        fetchLongwallsJarvisIncidents("active"),
        fetchLongwallsJarvisOperations(),
        fetchLongwallsJarvisAdapterHealth(),
        fetchLongwallsJarvisReleaseState(projectId || undefined),
        fetchLongwallsJarvisDefaultUsagePack(),
        fetchLongwallsJarvisLearning(),
        fetchLongwallsJarvisWeeklyBoard(),
      ]);

      const approvals = approvalsResult.status === "fulfilled" ? approvalsResult.value : [];
      const programs = programsResult.status === "fulfilled" ? programsResult.value : [];
      const incidents = incidentsResult.status === "fulfilled" ? incidentsResult.value : [];
      const operations = operationsResult.status === "fulfilled" ? operationsResult.value : [];
      const adapters = adaptersResult.status === "fulfilled" ? adaptersResult.value : overview.adapters;
      const releaseState = releaseStateResult.status === "fulfilled" ? releaseStateResult.value : null;
      const defaultUsage = defaultUsageResult.status === "fulfilled" ? defaultUsageResult.value : null;
      const learning = learningResult.status === "fulfilled" ? learningResult.value : {};
      const weeklyBoard = weeklyBoardResult.status === "fulfilled" ? weeklyBoardResult.value : {};

      const learningSummary = Array.isArray((learning as { recommendations?: unknown }).recommendations)
        ? ((learning as { recommendations: Array<{ title?: string; detail?: string }> }).recommendations ?? [])
            .slice(0, 3)
            .map((entry) => entry.title ?? entry.detail ?? "Learning recommendation")
        : [];

      const weeklyBoardLaneCount =
        typeof (weeklyBoard as { lanes?: unknown }).lanes === "object" && Array.isArray((weeklyBoard as { lanes?: unknown[] }).lanes)
          ? (weeklyBoard as { lanes: unknown[] }).lanes.length
          : null;

      if (refreshRequestRef.current !== requestId) return;
      setSnapshot({
        ...fallbackBridgeSnapshot(overview),
        approvals,
        programs,
        incidents,
        operations,
        adapters,
        releaseState,
        defaultUsage,
        learningSummary,
        weeklyBoardLaneCount,
      });
    } catch (cause) {
      if (refreshRequestRef.current !== requestId) return;
      const message = cause instanceof Error ? cause.message : "Failed to load Jarvis bridge overview.";
      setSnapshot(fallbackBridgeSnapshot(buildUnavailableOverview(message)));
      setError(message);
    } finally {
      if (refreshRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    void refresh();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    persistPilotChain(projectId, pilotChain);
  }, [pilotChain, projectId]);

  const heroMeta = useMemo(() => {
    if (!snapshot || (isLoading && snapshot.overview.readiness.overallScore === null)) {
      return [
        { label: "Bridge status", value: isLoading ? "Loading" : "Unavailable" },
        { label: "Pilot gate", value: projectId ? "Loading" : "No project" },
      ];
    }

    return [
      { label: "Bridge status", value: statusLabel(snapshot.overview.connection.status) },
      {
        label: "Readiness",
        value: snapshot.overview.readiness.overallScore === null ? "-" : `${snapshot.overview.readiness.overallScore}/100`,
      },
      { label: "Pilot gate", value: pilotGate ? `${pilotGate.score}/100` : projectId ? "Pending" : "No project" },
      { label: "Pending approvals", value: String(snapshot.overview.founder.pendingApprovals) },
    ];
  }, [isLoading, pilotGate, projectId, snapshot]);

  const fallbackPilotPackUrls = useMemo(
    () => (projectId ? buildPilotPackArtifactUrls(projectId) : null),
    [projectId],
  );

  const effectivePilotPack = useMemo<LongwallsEnterprisePilotPack | null>(() => {
    if (!projectId) return null;
    if (pilotPack) return pilotPack;

    const overview = snapshot?.overview ?? buildLoadingOverview();
    const fallbackUrls = fallbackPilotPackUrls ?? buildPilotPackArtifactUrls(projectId);
    const nextMove =
      (isPilotGateLoading ? "Pilot gate is still loading." : pilotGate?.blockers?.[0]) ??
      (isPilotChainLoading ? "Pilot chain is still loading." : pilotChain?.nextAction) ??
      "Refresh the pilot pack once the Atlas, Foundry, and Jarvis evidence chain stabilizes.";

    return {
      generatedAt: new Date().toISOString(),
      projectId,
      projectName: projectId,
      summary: {
        status: pilotGate?.status ?? "pilot_not_ready",
        score: pilotGate?.score ?? 0,
        headline: buildPilotPackFallbackHeadline(pilotGate?.status ?? "pilot_not_ready", projectId),
        sponsorNarrative:
          pilotGate?.status === "pilot_ready"
            ? "LongWalls keeps the sponsor and operator lane visible while the pilot dossier regenerates from already-green runtime truth."
            : "LongWalls keeps the sponsor and operator lane visible even when the packaged pilot dossier is not yet materialized.",
        operatingModel: "Atlas -> LongWalls Blueprint -> Jarvis controlled delivery",
        nextMove,
      },
      gate: {
        blockers: pilotGate?.blockers ?? [],
        nextActions: pilotGate?.nextActions ?? (nextMove ? [nextMove] : []),
        evidenceLinks: pilotGate?.evidenceLinks ?? [],
        gateBreakdown: pilotGate?.gateBreakdown ?? [],
      },
      atlas: {
        posture: "Sponsor outputs are present, but the packaged pilot dossier is not yet materialized.",
        prioritizedRoadmapUrl: `/api/roadmap/projects/${encodeURIComponent(projectId)}/prioritized`,
        prioritizedRoadmapPdfUrl: `/api/roadmap/projects/${encodeURIComponent(projectId)}/prioritized/export.pdf`,
        nextMove: "Keep the discovery slice bounded while the proof pack regenerates.",
      },
      foundry: {
        contractReady: pilotGate?.summary.contractReady ?? false,
        handoffReady: pilotGate?.summary.handoffReady ?? false,
        releaseStatus: pilotGate?.summary.releaseDecision ?? overview.readiness.releaseDecision ?? "unknown",
        contractArtifact: null,
        handoffPackage: null,
        releaseBundleJson: "",
        releaseBundleMarkdown: "",
      },
      jarvis: {
        bridgeStatus: overview.connection.status,
        chainState: pilotChain?.state ?? "not_started",
        releaseDecision: pilotGate?.summary.releaseDecision ?? overview.readiness.releaseDecision,
        pendingApprovals: overview.founder.pendingApprovals,
        openIncidents: overview.founder.openIncidents,
        founderLink: overview.links.founder,
        tacticalLink: overview.links.tactical,
        operatorLink: overview.links.operator,
      },
      sloBaseline: {
        greenRuns: pilotGate?.summary.greenRuns ?? 0,
        requiredGreenRuns: pilotGate?.summary.requiredGreenRuns ?? 3,
        readinessScore: overview.readiness.overallScore ?? 0,
        controlTowerScore: overview.readiness.overallScore ?? 0,
        artifactAssuranceScore: pilotGate?.score ?? 0,
        openIncidents: overview.founder.openIncidents,
        pendingApprovals: overview.founder.pendingApprovals,
      },
      artifactUrls: {
        pilotPackJson: fallbackUrls.pilotPackJson,
        pilotPackMarkdown: fallbackUrls.pilotPackMarkdown,
        roleGuide: fallbackUrls.roleGuide,
        supportRunbook: fallbackUrls.supportRunbook,
        deploymentKit: fallbackUrls.deploymentKit,
        executivePackJson: "",
        executivePackMarkdown: "",
        readinessPackJson: "",
        readinessPackMarkdown: "",
        releaseBundleJson: "",
        releaseBundleMarkdown: "",
      },
    };
  }, [
    fallbackPilotPackUrls,
    isPilotChainLoading,
    isPilotGateLoading,
    pilotChain?.nextAction,
    pilotChain?.state,
    pilotGate,
    pilotPack,
    projectId,
    snapshot,
  ]);

  const handleAction = async (message: string, action: () => Promise<unknown>) => {
    setIsMutating(true);
    setActionMessage(null);
    setError(null);
    try {
      await action();
      setActionMessage(message);
      void refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Jarvis action failed.");
    } finally {
      setIsMutating(false);
    }
  };

  const handlePilotChainAction = async (
    message: string,
    action: () => Promise<{
      pilotChain: LongwallsPilotChainRecord;
      pilotGate?: LongwallsEnterprisePilotGate | null;
    }>,
  ) => {
    setIsMutating(true);
    setActionMessage(null);
    setError(null);
    try {
      const result = await action();
      setPilotChain(result.pilotChain);
      if (result.pilotGate !== undefined) {
        setPilotGate(result.pilotGate);
      }
      setActionMessage(message);
      void refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Jarvis action failed.");
    } finally {
      setIsMutating(false);
    }
  };

  const waitForPilotChainState = async (
    targetStates: LongwallsPilotChainRecord["state"][],
    options: { previousState?: LongwallsPilotChainRecord["state"] | null; timeoutMs?: number; intervalMs?: number } = {},
  ) => {
    if (!projectId) {
      throw new Error("Project context is required for the pilot chain.");
    }

    const previousState = options.previousState ?? pilotChain?.state ?? null;
    const timeoutMs = options.timeoutMs ?? 45_000;
    const intervalMs = options.intervalMs ?? 1_000;
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      const latest = await fetchLongwallsPilotChain(projectId).catch(() => null);
      if (latest) {
        setPilotChain(latest);
      }
      if (latest && hasReachedPilotChainStage(latest.state, targetStates) && latest.state !== previousState) {
        return latest;
      }
      await sleep(intervalMs);
    }

    throw new Error(`Pilot chain did not reach ${targetStates.join(", ")} within ${timeoutMs}ms.`);
  };

  const launchPilotChainMutation = async (
    mutate: () => Promise<unknown>,
    targetStates: LongwallsPilotChainRecord["state"][],
    options: PilotChainMutationOptions = {},
  ): Promise<LongwallsPilotChainRecord> => {
    const previousState = pilotChain?.state ?? null;

    let mutationResult: unknown;
    try {
      mutationResult = await mutate();
    } catch (cause) {
      throw cause instanceof Error ? cause : new Error("Pilot chain mutation failed.");
    }

    if (isPilotChainRecord(mutationResult)) {
      setPilotChain(mutationResult);
      if (hasReachedPilotChainStage(mutationResult.state, targetStates) && mutationResult.state !== previousState) {
        return mutationResult;
      }
    }

    if (options.optimisticState) {
      setPilotChain((current) => {
        if (!current) return current;
        return {
          ...current,
          state: options.optimisticState!,
          updatedAt: new Date().toISOString(),
          nextAction: options.optimisticNextAction ?? current.nextAction,
        };
      });
    }

    return waitForPilotChainState(targetStates, {
      previousState,
      timeoutMs: options.timeoutMs ?? 60_000,
      intervalMs: options.intervalMs ?? 1_500,
    });
  };

  const submitFeedback = async () => {
    if (!projectId) return;
    await handlePilotChainAction("Pilot feedback captured.", async () => {
      const pilotChain = await launchPilotChainMutation(
        () =>
          submitLongwallsPilotFeedback(projectId, {
            author: "LongWalls Founder Review",
            summary: feedbackSummary.trim(),
            requestedChanges: toDisplayList(feedbackRequestedChanges),
            acceptanceNotes: toDisplayList(feedbackAcceptanceNotes),
            risks: toDisplayList(feedbackRisks),
          }),
        ["feedback_received", "blocked"],
        {
          timeoutMs: 90_000,
          optimisticState: "feedback_received",
          optimisticNextAction: "Launch the finalization run from the stored feedback packet.",
        },
      );
      setFeedbackSummary("");
      setFeedbackRequestedChanges("");
      setFeedbackAcceptanceNotes("");
      setFeedbackRisks("");
      return { pilotChain };
    });
  };

  const releaseHeadline =
    typeof snapshot?.releaseState?.summary === "object" && snapshot?.releaseState?.summary && "headline" in snapshot.releaseState.summary
      ? String(snapshot.releaseState.summary.headline ?? "")
      : snapshot?.overview.readiness.truthStatement ?? null;
  const pilotPackView = projectId && effectivePilotPack ? effectivePilotPack : null;
  const effectivePilotGate =
    projectId && pilotPackView
      ? (pilotGate ?? {
          generatedAt: new Date().toISOString(),
          projectId,
          status: "pilot_not_ready" as const,
          score: pilotPackView.summary.score,
          summary: {
            greenRuns: pilotPackView.sloBaseline.greenRuns,
            requiredGreenRuns: pilotPackView.sloBaseline.requiredGreenRuns,
            contractReady: pilotPackView.foundry.contractReady,
            handoffReady: pilotPackView.foundry.handoffReady,
            releaseDecision: pilotPackView.jarvis.releaseDecision,
            rollbackReady: false,
            sloBaseline: {
              readinessScore: pilotPackView.sloBaseline.readinessScore,
              controlTowerScore: pilotPackView.sloBaseline.controlTowerScore,
              artifactAssuranceScore: pilotPackView.sloBaseline.artifactAssuranceScore,
            },
          },
          blockers: [pilotChain?.nextAction ?? "Regenerate the pilot gate."],
          nextActions: [pilotChain?.nextAction ?? "Regenerate the pilot gate."],
          evidenceLinks: [],
          gateBreakdown: [],
        })
      : null;
  const pilotChainStatus = pilotChain?.state ?? (!isPilotChainLoading ? "not_started" : null);
  const pilotChainNextMove =
    pilotChain?.nextAction ??
    (!isPilotChainLoading ? "Bootstrap the pilot chain." : null);
  const catalogWiring = snapshot?.releaseState?.catalogWiring ?? null;
  const defaultUsageView = snapshot?.defaultUsage ?? null;
  const highlightedCatalogEntries = (catalogWiring?.entries ?? []).filter((entry) => entry.selection !== "rejected").slice(0, 6);
  const highlightedAssemblySteps = (catalogWiring?.assembly.steps ?? []).slice(0, 4);

  return (
    <div className="home-dashboard jarvis-dashboard mx-auto flex w-full max-w-[1480px] flex-col gap-6">
      <section className="home-dashboard__hero jarvis-dashboard__hero" data-testid="jarvis-bridge-hero">
        <div className="home-dashboard__hero-copy">
          <span className="home-dashboard__eyebrow">Governed delivery control layer</span>
          <div className="space-y-3">
            <h1 className="home-dashboard__title">Jarvis</h1>
            <p className="home-dashboard__subtitle">
              Founder-safe delivery console for approvals, runs, evidence, replay, finalization, and enterprise pilot truth.
            </p>
            {projectId ? (
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Project focus: {projectId}
              </div>
            ) : null}
          </div>
        </div>
        <div className="home-dashboard__hero-meta" aria-label="Jarvis bridge summary">
          {heroMeta.map((item) => (
            <div key={item.label} className="home-dashboard__meta-card">
              <span className="home-dashboard__meta-label">{item.label}</span>
              <strong className="home-dashboard__meta-value">{item.value}</strong>
            </div>
          ))}
        </div>
      </section>

      {error ? (
        <Card className="border border-amber-200" bodyClassName="space-y-3">
          <div className="text-sm font-semibold text-slate-900">Jarvis signal degraded</div>
          <p className="text-sm text-slate-700">{error}</p>
          <p className="text-xs text-slate-500">
            LongWalls remains usable, but the governed delivery chain is currently incomplete or unreachable.
          </p>
        </Card>
      ) : null}

      {actionMessage ? (
        <Card className="border border-emerald-200 bg-emerald-50" bodyClassName="text-sm text-emerald-700">
          {actionMessage}
        </Card>
      ) : null}

      {snapshot ? (
        <>
          <section className="jarvis-bridge__grid" data-testid="jarvis-bridge-grid">
            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Founder signal</div>
                  <div className="jarvis-bridge__panel-title">Decision posture</div>
                </div>
                <span className={`jarvis-bridge__status jarvis-bridge__status--${snapshot.overview.connection.status}`}>
                  {statusLabel(snapshot.overview.connection.status)}
                </span>
              </div>
              <div className="jarvis-bridge__stats">
                <div>
                  <span className="jarvis-bridge__stat-label">Release decision</span>
                  <strong className="jarvis-bridge__stat-value">
                    {snapshot.overview.readiness.releaseDecision?.toUpperCase() ?? "UNKNOWN"}
                  </strong>
                </div>
                <div>
                  <span className="jarvis-bridge__stat-label">Leverage score</span>
                  <strong className="jarvis-bridge__stat-value">
                    {snapshot.overview.founder.leverageScore === null ? "-" : snapshot.overview.founder.leverageScore}
                  </strong>
                </div>
                <div>
                  <span className="jarvis-bridge__stat-label">Open incidents</span>
                  <strong className="jarvis-bridge__stat-value">{snapshot.overview.founder.openIncidents}</strong>
                </div>
              </div>
              <p className="text-sm text-slate-600">{releaseHeadline}</p>
              <div className="jarvis-bridge__signal-list">
                {snapshot.overview.founder.criticalSignals.length ? (
                  snapshot.overview.founder.criticalSignals.map((signal) => (
                    <div key={signal} className="jarvis-bridge__signal">
                      {signal}
                    </div>
                  ))
                ) : (
                  <div className="jarvis-bridge__signal">No critical founder signals are currently open.</div>
                )}
              </div>
              {snapshot.learningSummary.length ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Learning signal</div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    {snapshot.learningSummary.map((entry) => (
                      <div key={entry}>{entry}</div>
                    ))}
                  </div>
                </div>
              ) : null}
            </Card>

            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Operator signal</div>
                  <div className="jarvis-bridge__panel-title">Security and runtime posture</div>
                </div>
              </div>
              <div className="jarvis-bridge__stats">
                <div>
                  <span className="jarvis-bridge__stat-label">Auth</span>
                  <strong className="jarvis-bridge__stat-value">{snapshot.overview.operator.authProvider ?? "-"}</strong>
                </div>
                <div>
                  <span className="jarvis-bridge__stat-label">Store</span>
                  <strong className="jarvis-bridge__stat-value">{snapshot.overview.operator.storeBackend ?? "-"}</strong>
                </div>
                <div>
                  <span className="jarvis-bridge__stat-label">Break-glass</span>
                  <strong className="jarvis-bridge__stat-value">
                    {snapshot.overview.security.breakGlassActive ? "ACTIVE" : "OFF"}
                  </strong>
                </div>
              </div>
              <div className="jarvis-bridge__credential-list" data-testid="jarvis-credential-posture">
                {snapshot.overview.security.credentialStatus.map((entry) => (
                  <div key={entry.id} className="jarvis-bridge__credential">
                    <div className="jarvis-bridge__credential-top">
                      <span className="jarvis-bridge__credential-name">{entry.label}</span>
                      <span className={`jarvis-bridge__credential-status jarvis-bridge__credential-status--${entry.status}`}>
                        {entry.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{entry.detail}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                Weekly board lanes observed: {snapshot.weeklyBoardLaneCount ?? "n/a"} | Adapters healthy:{" "}
                {snapshot.overview.operator.healthyAdapters}/{snapshot.overview.operator.totalAdapters}
              </div>
            </Card>
          </section>

          <section className="jarvis-bridge__grid" data-testid="jarvis-default-usage">
            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Jarvis-first control tower</div>
                  <div className="jarvis-bridge__panel-title">Review, replay, runtime, and pilot governance start here</div>
                </div>
                <span
                  className={`jarvis-bridge__status jarvis-bridge__status--${
                    defaultUsageView?.controlTower.status === "locked"
                      ? "connected"
                      : defaultUsageView?.status === "default_usage_locked"
                        ? "degraded"
                        : "offline"
                  }`}
                >
                  {defaultUsageView?.controlTower.status ?? (defaultUsageView?.status === "default_usage_locked" ? "watch" : "building")}
                </span>
              </div>
              {defaultUsageView ? (
                <>
                  <div className="jarvis-bridge__stats">
                    <div>
                      <span className="jarvis-bridge__stat-label">Default surface</span>
                      <strong className="jarvis-bridge__stat-value">{defaultUsageView.summary.defaultSurface}</strong>
                    </div>
                    <div>
                      <span className="jarvis-bridge__stat-label">Room coverage</span>
                      <strong className="jarvis-bridge__stat-value">{defaultUsageView.summary.roomCoverageScore}/100</strong>
                    </div>
                    <div>
                      <span className="jarvis-bridge__stat-label">Command coverage</span>
                      <strong className="jarvis-bridge__stat-value">{defaultUsageView.summary.commandCoverageScore}/100</strong>
                    </div>
                    <div>
                      <span className="jarvis-bridge__stat-label">Control tower score</span>
                      <strong className="jarvis-bridge__stat-value">{defaultUsageView.summary.controlTowerScore}/100</strong>
                    </div>
                    <div>
                      <span className="jarvis-bridge__stat-label">Latency proxy</span>
                      <strong className="jarvis-bridge__stat-value">
                        {defaultUsageView.summary.decisionLatencyReductionPercent}% faster
                      </strong>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{defaultUsageView.summary.headline}</p>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    {defaultUsageView.summary.narrative}
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    {defaultUsageView.roleRoutines.map((routine) => (
                      <div key={routine.role} className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {routine.role === "founder"
                                ? "Founder default review"
                                : routine.role === "tactical"
                                  ? "Tactical default review"
                                  : "Operator default review"}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">{routine.room}</div>
                          </div>
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            {routine.readiness}
                          </span>
                        </div>
                        <div className="mt-3 text-xs text-slate-600">{routine.defaultQuestion}</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {routine.requiredSignals.map((signal) => (
                            <span
                              key={`${routine.role}-${signal}`}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                            >
                              {signal}
                            </span>
                          ))}
                        </div>
                        <div className="mt-3 text-xs text-slate-500">{routine.cadence}</div>
                        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                          {routine.fallbackIfMissing}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    Surface switches {defaultUsageView.decisionLatency.currentSurfaceCount}/
                    {defaultUsageView.decisionLatency.baselineSurfaceCount} · Review minutes{" "}
                    {defaultUsageView.decisionLatency.currentReviewMinutes}/
                    {defaultUsageView.decisionLatency.baselineReviewMinutes}
                  </div>
                  <div className="grid gap-3 xl:grid-cols-[1.08fr,0.92fr]">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Control tower loops</div>
                      <div className="mt-3 space-y-3">
                        {defaultUsageView.controlTower.loops.map((loop) => (
                          <div key={loop.id} className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{loop.title}</div>
                                <div className="mt-1 text-xs text-slate-500">
                                  {loop.firstSurface} {"->"} {loop.supportingSurface}
                                </div>
                              </div>
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                {loop.readiness}
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-slate-600">{loop.trigger}</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {loop.actions.map((action) => (
                                <span
                                  key={`${loop.id}-${action}`}
                                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                                >
                                  {action}
                                </span>
                              ))}
                            </div>
                            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                              Proof: {loop.proofObjects.join(", ")}
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500">{loop.blockerIfMissing}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pilot governance</div>
                        <div className="mt-2 text-sm font-semibold text-slate-900">
                          {defaultUsageView.pilotGovernance.headline}
                        </div>
                        <div className="mt-2 text-xs text-slate-600">
                          Status: {defaultUsageView.pilotGovernance.status} | Start surface:{" "}
                          {defaultUsageView.pilotGovernance.reviewStartSurface}
                        </div>
                        {projectId ? (
                          <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                            Project control path: pilot gate {effectivePilotGate?.status ?? "pending"} | pilot chain{" "}
                            {pilotChainStatus ?? "pending"}
                            {isPilotChainLoading && pilotChain ? " | refreshing" : ""}
                          </div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {defaultUsageView.pilotGovernance.commandPath.map((step) => (
                            <span
                              key={step}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                            >
                              {step}
                            </span>
                          ))}
                        </div>
                        <div className="mt-3 space-y-2">
                          {defaultUsageView.pilotGovernance.requiredChecks.map((item) => (
                            <div key={item} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                              Check: {item}
                            </div>
                          ))}
                          {defaultUsageView.pilotGovernance.freezeConditions.map((item) => (
                            <div key={item} className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700">
                              Freeze: {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Surface policy</div>
                        <div className="mt-3 space-y-2">
                          {defaultUsageView.surfacePolicy.requiredProofBeforeExit.map((item) => (
                            <div key={item} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                              Proof before exit: {item}
                            </div>
                          ))}
                          {defaultUsageView.surfacePolicy.allowedEscalations.map((item) => (
                            <div key={item} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                              Allowed escalation: {item}
                            </div>
                          ))}
                          {defaultUsageView.surfacePolicy.prohibitedExits.map((item) => (
                            <div key={item} className="rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-700">
                              Prohibited exit: {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a className="text-xs font-medium text-blue-700 underline" href={defaultUsageView.artifactUrls.playbook}>
                      default usage playbook
                    </a>
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={defaultUsageView.artifactUrls.routinesMatrix}
                    >
                      review routines matrix
                    </a>
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={defaultUsageView.artifactUrls.latencyScorecard}
                    >
                      decision latency scorecard
                    </a>
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={defaultUsageView.artifactUrls.controlTower}
                    >
                      control tower scorecard
                    </a>
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={defaultUsageView.artifactUrls.pilotGovernance}
                    >
                      pilot governance map
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-600">
                  Default review usage is still loading. Jarvis should become the first control surface once the pack regenerates.
                </div>
              )}
            </Card>
          </section>

          {projectId && effectivePilotPack ? (
            <section className="jarvis-bridge__grid" data-testid="jarvis-pilot-grid">
              <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
                <div className="jarvis-bridge__panel-header">
                  <div>
                    <div className="jarvis-bridge__panel-eyebrow">Enterprise pilot gate</div>
                    <div className="jarvis-bridge__panel-title">Archimedes A→Z proof</div>
                  </div>
                </div>
                {effectivePilotGate ? (
                  <>
                    <div className="jarvis-bridge__stats">
                      <div>
                        <span className="jarvis-bridge__stat-label">Status</span>
                        <strong className="jarvis-bridge__stat-value">{effectivePilotGate.status}</strong>
                      </div>
                      <div>
                        <span className="jarvis-bridge__stat-label">Score</span>
                        <strong className="jarvis-bridge__stat-value">{effectivePilotGate.score}/100</strong>
                      </div>
                      <div>
                        <span className="jarvis-bridge__stat-label">Green runs</span>
                        <strong className="jarvis-bridge__stat-value">
                          {effectivePilotGate.summary.greenRuns}/{effectivePilotGate.summary.requiredGreenRuns}
                        </strong>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-slate-700" data-testid="jarvis-pilot-gate">
                      {(
                        isPilotGateLoading
                          ? ["Pilot gate is still loading."]
                          : effectivePilotGate.blockers.length
                            ? effectivePilotGate.blockers
                            : effectivePilotGate.nextActions
                      )
                        .slice(0, 4)
                        .map((item) => (
                        <div key={item}>• {item}</div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-600">Pilot gate will appear once the project context is available.</div>
                )}
              </Card>

              <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
                <div className="jarvis-bridge__panel-header">
                  <div>
                    <div className="jarvis-bridge__panel-eyebrow">Pilot chain</div>
                    <div className="jarvis-bridge__panel-title">Bootstrap, V1, feedback, finalization</div>
                  </div>
                </div>
                {pilotChainStatus ? (
                  <div className="text-sm text-slate-700" data-testid="jarvis-pilot-chain">
                    State: {pilotChainStatus} | Next move: {pilotChainNextMove}
                    {isPilotChainLoading && pilotChain ? " | Refreshing…" : ""}
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">Loading pilot chain truth…</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void handlePilotChainAction("Pilot chain bootstrapped.", async () => {
                        const pilotChain = await launchPilotChainMutation(
                          () => bootstrapLongwallsPilotChain(projectId),
                          ["bootstrapped", "blocked"],
                          {
                            timeoutMs: 45_000,
                          },
                        );
                        return { pilotChain };
                      })
                    }
                    disabled={isMutating || isPilotChainLoading}
                  >
                    Bootstrap pilot chain
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void handlePilotChainAction("Pilot V1 run launched.", async () => {
                        const pilotChain = await launchPilotChainMutation(
                          () => runLongwallsPilotV1(projectId),
                          ["v1_ready", "blocked"],
                          {
                            timeoutMs: 120_000,
                            optimisticState: "v1_running",
                            optimisticNextAction: "Wait for the V1 run to finish and inspect the evidence bundle.",
                          },
                        );
                        return { pilotChain };
                      })
                    }
                    disabled={isMutating || isPilotChainLoading}
                  >
                    Run V1
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void handlePilotChainAction("Pilot finalization launched.", async () => {
                        const pilotChain = await launchPilotChainMutation(
                          () => finalizeLongwallsPilotChain(projectId),
                          ["finalized", "blocked", "finalizing"],
                          {
                            timeoutMs: 120_000,
                            optimisticState: "finalizing",
                            optimisticNextAction: "Wait for the finalization run and review release evidence.",
                          },
                        );
                        return { pilotChain };
                      })
                    }
                    disabled={isMutating || isPilotChainLoading || !pilotChain?.finalizationReady}
                  >
                    Finalize pilot
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm text-slate-700 md:col-span-2">
                    Founder feedback summary
                    <textarea
                      value={feedbackSummary}
                      onChange={(event) => setFeedbackSummary(event.target.value)}
                      className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="One structured human feedback packet before finalization."
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Requested changes
                    <textarea
                      value={feedbackRequestedChanges}
                      onChange={(event) => setFeedbackRequestedChanges(event.target.value)}
                      className="min-h-16 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="One per line"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Acceptance notes
                    <textarea
                      value={feedbackAcceptanceNotes}
                      onChange={(event) => setFeedbackAcceptanceNotes(event.target.value)}
                      className="min-h-16 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="One per line"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700 md:col-span-2">
                    Risks observed
                    <textarea
                      value={feedbackRisks}
                      onChange={(event) => setFeedbackRisks(event.target.value)}
                      className="min-h-16 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="One per line"
                    />
                  </label>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void submitFeedback()}
                  disabled={isMutating || isPilotChainLoading || !feedbackSummary.trim()}
                >
                  Capture feedback packet
                </Button>
              </Card>
            </section>
          ) : null}

          {pilotPackView ? (
            <section className="jarvis-bridge__grid" data-testid="jarvis-pilot-pack">
              <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
                <div className="jarvis-bridge__panel-header">
                  <div>
                    <div className="jarvis-bridge__panel-eyebrow">Pilot pack</div>
                    <div className="jarvis-bridge__panel-title">Sponsor and operator package</div>
                  </div>
                  <span
                    className={`jarvis-bridge__status jarvis-bridge__status--${pilotPackStatusTone(pilotPackView.summary.status)}`}
                    title={`Pilot pack status: ${pilotPackView.summary.status}`}
                  >
                    {pilotPackStatusLabel(pilotPackView.summary.status)}
                  </span>
                </div>
                <div className="jarvis-bridge__stats">
                  <div>
                    <span className="jarvis-bridge__stat-label">Pack score</span>
                    <strong className="jarvis-bridge__stat-value">{pilotPackView.summary.score}/100</strong>
                  </div>
                  <div>
                    <span className="jarvis-bridge__stat-label">Green runs</span>
                    <strong className="jarvis-bridge__stat-value">
                      {pilotPackView.sloBaseline.greenRuns}/{pilotPackView.sloBaseline.requiredGreenRuns}
                    </strong>
                  </div>
                  <div>
                    <span className="jarvis-bridge__stat-label">Incidents</span>
                    <strong className="jarvis-bridge__stat-value">
                      {pilotPackView.sloBaseline.openIncidents}
                    </strong>
                  </div>
                </div>
                <p className="text-sm text-slate-600">{pilotPackView.summary.headline}</p>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  {pilotPackView.summary.sponsorNarrative}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Atlas</div>
                    <div className="mt-2 text-sm text-slate-700">{pilotPackView.atlas.posture}</div>
                    <div className="mt-2 text-xs text-slate-500">{pilotPackView.atlas.nextMove}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Foundry</div>
                    <div className="mt-2 text-sm text-slate-700">
                      Contract {pilotPackView.foundry.contractReady ? "ready" : "blocked"} · Handoff{" "}
                      {pilotPackView.foundry.handoffReady ? "ready" : "blocked"} · Release {pilotPackView.foundry.releaseStatus}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">{pilotPackView.summary.operatingModel}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(pilotPackView.artifactUrls.pilotPackMarkdown, "_blank", "noopener,noreferrer")}
                  >
                    Open pilot pack
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(pilotPackView.artifactUrls.roleGuide, "_blank", "noopener,noreferrer")}
                  >
                    Open role guide
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(pilotPackView.artifactUrls.supportRunbook, "_blank", "noopener,noreferrer")}
                  >
                    Open support runbook
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(pilotPackView.artifactUrls.deploymentKit, "_blank", "noopener,noreferrer")}
                  >
                    Open deployment kit
                  </Button>
                </div>
              </Card>
            </section>
          ) : null}

          {catalogWiring ? (
            <section className="jarvis-bridge__grid" data-testid="jarvis-catalog-wiring">
              <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
                <div className="jarvis-bridge__panel-header">
                  <div>
                    <div className="jarvis-bridge__panel-eyebrow">Catalog wiring</div>
                    <div className="jarvis-bridge__panel-title">Frozen brick contract carried into Jarvis</div>
                  </div>
                  <span
                    className={`jarvis-bridge__status ${catalogWiring.status === "assembled" ? "jarvis-bridge__status--connected" : "jarvis-bridge__status--degraded"}`}
                  >
                    {catalogWiring.status === "assembled" ? "Assembled" : "Frozen"}
                  </span>
                </div>
                <div className="jarvis-bridge__stats">
                  <div>
                    <span className="jarvis-bridge__stat-label">Required</span>
                    <strong className="jarvis-bridge__stat-value">{catalogWiring.counts.required}</strong>
                  </div>
                  <div>
                    <span className="jarvis-bridge__stat-label">Recommended</span>
                    <strong className="jarvis-bridge__stat-value">{catalogWiring.counts.recommended}</strong>
                  </div>
                  <div>
                    <span className="jarvis-bridge__stat-label">Sensitive</span>
                    <strong className="jarvis-bridge__stat-value">{catalogWiring.sensitiveBrickIds.length}</strong>
                  </div>
                  <div>
                    <span className="jarvis-bridge__stat-label">Assembly</span>
                    <strong className="jarvis-bridge__stat-value">
                      {catalogWiring.assembly.progress.passed}/{catalogWiring.assembly.progress.total}
                    </strong>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  {catalogWiring.summary.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Runtime truth</div>
                    <div className="mt-2">App: {catalogWiring.appName ?? catalogWiring.projectId}</div>
                    <div className="mt-1">Handoff: {catalogWiring.handoffId ?? "-"}</div>
                    <div className="mt-1">Latest run: {catalogWiring.latestRunId ?? "none yet"}</div>
                    <div className="mt-1">Archetype: {catalogWiring.archetypeId ?? "-"}</div>
                    <div className="mt-1">Delivery mode: {catalogWiring.assembly.deliveryMode}</div>
                    <div className="mt-1">Domain family: {catalogWiring.assembly.domainFamily ?? "-"}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Artifact trace</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {catalogWiring.artifactUrls.handoffPackage ? (
                        <a className="text-xs font-medium text-blue-700 underline" href={catalogWiring.artifactUrls.handoffPackage}>
                          handoff_package.json
                        </a>
                      ) : null}
                      {catalogWiring.artifactUrls.executionSnapshot ? (
                        <a className="text-xs font-medium text-blue-700 underline" href={catalogWiring.artifactUrls.executionSnapshot}>
                          execution_snapshot.json
                        </a>
                      ) : null}
                      {catalogWiring.artifactUrls.deployHandoff ? (
                        <a className="text-xs font-medium text-blue-700 underline" href={catalogWiring.artifactUrls.deployHandoff}>
                          deploy_handoff.json
                        </a>
                      ) : null}
                      {catalogWiring.artifactUrls.deliveryResult ? (
                        <a className="text-xs font-medium text-blue-700 underline" href={catalogWiring.artifactUrls.deliveryResult}>
                          delivery_result.json
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Assembly truth</div>
                    <div className="mt-2">
                      Status:{" "}
                      <span className="font-semibold">
                        {catalogWiring.assembly.status}
                      </span>
                    </div>
                    <div className="mt-1">Primary flows: {catalogWiring.assembly.primaryFlowIds.join(", ") || "-"}</div>
                    <div className="mt-1">
                      Build blockers: {catalogWiring.assembly.buildBlockers.blocking} blocking · {catalogWiring.assembly.buildBlockers.watch} watch
                    </div>
                    <div className="mt-1">
                      Security pack: {catalogWiring.assembly.securityPack?.title ?? "-"}
                    </div>
                    {catalogWiring.assembly.securityPack ? (
                      <>
                        <div className="mt-1">RBAC template: {catalogWiring.assembly.securityPack.rbacTemplateId}</div>
                        <div className="mt-1">Tenant isolation: {catalogWiring.assembly.securityPack.tenantIsolationMode}</div>
                        <div className="mt-1">Secret posture: {catalogWiring.assembly.securityPack.secretHandlingMode}</div>
                        <div className="mt-1">SSO posture: {catalogWiring.assembly.securityPack.ssoProfile}</div>
                      </>
                    ) : null}
                    <div className="mt-1">QA pack: {catalogWiring.assembly.qaPack?.title ?? "-"}</div>
                    {catalogWiring.assembly.customDeltaSummary ? (
                      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                        {catalogWiring.assembly.customDeltaSummary}
                      </div>
                    ) : null}
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                        Depth score: <span className="font-semibold text-slate-900">{catalogWiring.assembly.divergenceProfile.depthScore}</span>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                        Divergence score: <span className="font-semibold text-slate-900">{catalogWiring.assembly.divergenceProfile.divergenceScore}</span>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                        Reuse ratio: <span className="font-semibold text-slate-900">{catalogWiring.assembly.divergenceProfile.reuseRatio}%</span>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                        Custom load: <span className="font-semibold text-slate-900">{catalogWiring.assembly.customDeltaTrace.load}</span>
                      </div>
                    </div>
                    {catalogWiring.assembly.divergenceProfile.distinguishingAttributes.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {catalogWiring.assembly.divergenceProfile.distinguishingAttributes.map((attribute) => (
                          <span
                            key={attribute}
                            className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                          >
                            {attribute}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Assembly evidence</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {catalogWiring.assembly.qualityGates.map((gate) => (
                        <span key={gate} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                          {gate}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {catalogWiring.assembly.evidenceArtifacts.map((artifact) => (
                        <span key={artifact} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                          {artifact}
                        </span>
                      ))}
                      {catalogWiring.assembly.securityPack?.evidenceBundleArtifacts.map((artifact) => (
                        <span key={`security-${artifact}`} className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                          {artifact}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {highlightedCatalogEntries.map((entry) => (
                    <span
                      key={`${entry.selection}-${entry.brickId}`}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${brickSelectionTone(entry.selection)}`}
                      title={entry.rationale.join(" ")}
                    >
                      {entry.title}
                    </span>
                  ))}
                </div>
                {highlightedAssemblySteps.length ? (
                  <div className="grid gap-2 md:grid-cols-2">
                    {highlightedAssemblySteps.map((step) => (
                      <div key={step.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
                        <div className="flex items-center justify-between gap-2">
                          <strong>{step.title}</strong>
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{step.status}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {step.kind} · {step.riskTier} · attempts {step.attempts}/{Math.max(1, step.retryBudget + 1)}
                        </div>
                        {step.reason ? <div className="mt-2 text-xs text-red-600">{step.reason}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </Card>
            </section>
          ) : null}

          <section className="jarvis-bridge__grid">
            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Approvals</div>
                  <div className="jarvis-bridge__panel-title">Founder-sensitive operations</div>
                </div>
              </div>
              <div className="jarvis-bridge__mini-list">
                {snapshot.approvals.length ? (
                  snapshot.approvals.slice(0, 5).map((approval) => (
                    <div key={approval.id} className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <strong>{approval.title}</strong>
                          <div className="text-xs text-slate-500">{approval.riskLevel ?? "risk n/a"}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleAction("Approval accepted.", () => approveLongwallsJarvisApproval(approval.id, { decisionBy: "LongWalls Founder" }))}
                            disabled={isMutating}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleAction("Approval rejected.", () => rejectLongwallsJarvisApproval(approval.id, { decisionBy: "LongWalls Founder" }))}
                            disabled={isMutating}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="jarvis-bridge__mini-item">
                    <strong>No approval queue</strong>
                    <span>Founder-sensitive operations are currently clear.</span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Incidents and runtime</div>
                  <div className="jarvis-bridge__panel-title">Operator control surface</div>
                </div>
              </div>
              <div className="jarvis-bridge__mini-list">
                {snapshot.incidents.length ? (
                  snapshot.incidents.slice(0, 4).map((incident) => (
                    <div key={incident.id} className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <strong>{incident.title}</strong>
                          <div className="text-xs text-slate-500">
                            {incident.severity ?? "watch"} · {incident.status ?? "unknown"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleAction("Incident acknowledged.", () => acknowledgeLongwallsJarvisIncident(incident.id, { resolvedBy: "LongWalls Operator" }))}
                            disabled={isMutating}
                          >
                            Ack
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleAction("Incident resolved.", () => resolveLongwallsJarvisIncident(incident.id, { resolvedBy: "LongWalls Operator" }))}
                            disabled={isMutating}
                          >
                            Resolve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="jarvis-bridge__mini-item">
                    <strong>No active incidents</strong>
                    <span>Runtime posture is currently stable.</span>
                  </div>
                )}
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                Release state:{" "}
                {typeof snapshot.releaseState?.summary === "object" && snapshot.releaseState?.summary && "releaseStatus" in snapshot.releaseState.summary
                  ? String(snapshot.releaseState.summary.releaseStatus ?? "unknown")
                  : "unknown"}
              </div>
            </Card>
          </section>

          <section className="jarvis-bridge__grid">
            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Programs and replay</div>
                  <div className="jarvis-bridge__panel-title">Tactical execution</div>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-700">
                {snapshot.programs.slice(0, 4).map((program) => (
                  <div key={program.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <strong>{program.name ?? program.id}</strong>
                    <div className="text-xs text-slate-500">
                      open {program.openTasks ?? 0} · blocked {program.blockedTasks ?? 0} · founder pending{" "}
                      {program.founderPendingTasks ?? 0}
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {snapshot.operations.slice(0, 3).map((operation) => (
                  <div key={operation.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <div>
                      <strong>{operation.title ?? operation.type ?? operation.id}</strong>
                      <div className="text-xs text-slate-500">{operation.status ?? "status unavailable"}</div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleAction("Operation replay launched.", () => replayLongwallsJarvisOperation(operation.id, { requestedBy: { source: "tactical", roleId: "longwalls-enterprise", projectId: projectId || null }, dryRun: true }))}
                      disabled={isMutating}
                    >
                      Replay
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="jarvis-bridge__panel" bodyClassName="space-y-4">
              <div className="jarvis-bridge__panel-header">
                <div>
                  <div className="jarvis-bridge__panel-eyebrow">Autonomy</div>
                  <div className="jarvis-bridge__panel-title">Overnight lane</div>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                {snapshot.overview.founder.overnightHeadline ?? "No overnight headline available yet."}
              </p>
              <div className="space-y-1 text-sm text-slate-700">
                {snapshot.adapters.slice(0, 4).map((adapter) => (
                  <div key={adapter.id ?? adapter.adapterId ?? adapter.detail}>
                    {(adapter.id ?? adapter.adapterId ?? "adapter")}: {adapter.status} · {adapter.detail}
                  </div>
                ))}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleAction("Overnight lane launched.", () => executeLongwallsJarvisOvernight({ requestedBy: { source: "tactical", roleId: "longwalls-enterprise", projectId: projectId || null } }))}
                disabled={isMutating}
              >
                Launch bounded overnight
              </Button>
            </Card>
          </section>

          <section className="jarvis-bridge__actions">
            <Button variant="secondary" size="sm" onClick={() => navigate("/portfolio_runs")}>
              Open portfolio runs
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate("/portfolio")}>
              Open portfolio
            </Button>
            {projectId ? (
              <Button variant="secondary" size="sm" onClick={() => navigate(`/foundry?projectId=${encodeURIComponent(projectId)}`)}>
                Open Foundry contract
              </Button>
            ) : null}
            {snapshot.overview.links.founder ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => window.open(snapshot.overview.links.founder ?? "", "_blank", "noopener,noreferrer")}
              >
                Open live Jarvis room
              </Button>
            ) : null}
          </section>
        </>
      ) : isLoading ? (
        <Card className="jarvis-bridge__panel" bodyClassName="space-y-2">
          <div className="text-sm font-semibold text-slate-900">Loading Jarvis bridge</div>
          <p className="text-sm text-slate-600">
            LongWalls is checking the live Jarvis command layer before exposing founder and operator signals.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
